import { getAnalytics } from "@tm/insurance/analytics";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BannerContent,
  BannerSlide,
  BannerWrapper,
  CarouselContainer,
  Dot,
  DotsContainer,
} from "./BannerCarousel.style";

interface Banner {
  imageUrl: string;
  navigationUrl: string;
}

interface BannerCarouselProps {
  banners?: Banner[];
  autoScrollInterval?: number; // in milliseconds
}

const defaultBanners: Banner[] = [
  {
    imageUrl:
      "https://tripmoneycmsimgak.mmtcdn.com/img/Group_1321318094_0b70f9084f.png",
    navigationUrl: "https://www.google.com",
  },
  {
    imageUrl:
      "https://tripmoneycmsimgak.mmtcdn.com/img/Group_1321318094_0b70f9084f.png",
    navigationUrl: "https://www.google.com",
  },
];

const BannerCarousel: React.FC<BannerCarouselProps> = ({
  banners = defaultBanners,
  autoScrollInterval = 3000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "marketing_banner_carousel_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: bannerCarouselRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
  }, [banners.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  }, [banners.length]);

  // Auto-scroll functionality
  useEffect(() => {
    if (isHovered || banners.length <= 1) return;

    const intervalId = setInterval(() => {
      goToNext();
    }, autoScrollInterval);

    return () => clearInterval(intervalId);
  }, [isHovered, autoScrollInterval, goToNext, banners.length]);

  // Touch/swipe functionality
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const handleBannerClick = (navigationUrl: string) => {
    if (navigationUrl) {
      window.open(navigationUrl, "_blank");
    }
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <CarouselContainer
      ref={bannerCarouselRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <BannerWrapper currentIndex={currentIndex}>
        {banners.map((banner, index) => (
          <BannerSlide
            key={index}
            onClick={() => handleBannerClick(banner.navigationUrl)}
          >
            <BannerContent src={banner.imageUrl} />
          </BannerSlide>
        ))}
      </BannerWrapper>

      <DotsContainer>
        {banners.map((_, index) => (
          <Dot
            key={index}
            active={index === currentIndex}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </DotsContainer>
    </CarouselContainer>
  );
};

export default BannerCarousel;
